import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { RecentlyViewedModule } from '@df/catalog-ui/recently-viewed/recently-viewed.module';
import { DfCoreModule } from '@df/core/df-core.module';
import { DfDomModule } from '@df/dom/df-dom.module';
import { StickyPanelDirective } from '@df/legacy/ui/sticky/sticky-panel.directive';
import { GtmImpressionTrackDirective } from '@df/module-gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '@df/module-gtm/gtm-track-product-click.directive';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { InfiniteScrollModule } from '@df/module-infinite-scroll/infinite-scroll.module';
import { KlevuTrackCategoryProductClickDirective } from '@df/module-klevu-search/klevu-track-category-product-click.directive';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { DfModulePrivacyModule } from '@df/module-privacy/df-module-privacy.module';
import { surviveTreeShaking } from '@df/ng/survive-tree-shaking';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { DragonflyStylesModule } from '@df/styles/dragonfly-styles.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { NoticePipe } from '@df/ui/api/notice/notice.pipe';
import { AtomicModule } from '@df/ui/atomic/atomic.module';
import { ClickEventDirective } from '@df/ui/common/click-event.directive';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { FilterMediaByLabelPipe } from '@df/ui/common/filter-media-by-label-pipe';
import { FindMediaByLabellPipe } from '@df/ui/common/find-media-by-label.pipe';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfAclDirective } from '@df/ui/common/if-acl.directive';
import { IfDevDirective } from '@df/ui/common/if-dev.directive';
import { LastCategoryPathPipe } from '@df/ui/common/last-category-path.pipe';
import { LineHeightByComponent } from '@df/ui/common/line-height-by.component';
import { Nl2brPipe } from '@df/ui/common/nl2br.pipe';
import { OnInViewDirective } from '@df/ui/common/on-in-view.directive';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { ToAddressPipe } from '@df/ui/common/to-address.pipe';
import { DfUiModule } from '@df/ui/df-ui.module';
import { FormatDatePipe } from '@df/ui/display/format-date.pipe';
import { HourFormat12Pipe } from '@df/ui/display/hour-format-12.pipe';
import { IsWhitePipe } from '@df/ui/display/is-white.pipe';
import { MediaModule } from '@df/ui/display/media/media.module';
import { AutoSizeInputDirective } from '@df/ui/form/auto-size-input.directive';
import { FileUploadComponent } from '@df/ui/form/file-upload/file-upload.component';
import { MatchCheckDirective } from '@df/ui/form/match-check.directive';
import { ValidateNumberDirective } from '@df/ui/form/validate-number.directive';
import { ValidatePatternDirective } from '@df/ui/form/validate-pattern.directive';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ModalModule } from '@df/ui/modal/modal.module';
import { DecimalFormatDirective } from '@df/ui/number-format.directive';
import { PhotoSwipeModule } from '@df/ui/photoswipe/photoswipe.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { PricePipe } from '@df/ui/price/price.pipe';
import { ToggleClassDirective } from '@df/ui/toggle/toggle-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ScrollToDirective } from '@df/ui/util/scroll/scroll-to.directive';
import { VarsPipe } from '@df/ui/util/vars.pipe';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { SwiperModule } from 'swiper/angular';
import { AccountHistoryInvoicesPaginationComponent } from './account/history/invoices-pagination/account-history-invoices-pagination.component';
import { RemoveCouponComponent } from './basket/view/coupon-form/remove-coupon.component';
import { BranchCollectionComponent } from './branch/collection/branch-collection.component';
import { BranchFinderComponent } from './branch/finder/branch-finder.component';
import { BranchFinderItemComponent } from './branch/finder/item/branch-finder-item.component';
import { LoadMoreComponent } from './catalog/listing/load-more/load-more.component';
import { ICmsOutletBlockComponent } from './catalog/outlet-block/icms-outlet-block.component';
import { PageStoreFinderStoreStatusComponent } from './catalog/page/store-finder/store-status/page-store-finder-store-status.component';
import { PageStoreFinderStoreComponent } from './catalog/page/store-finder/store/page-store-finder-store.component';
import { TradePricingModule } from './checkout/module/trade/pricing/trade-pricing.module';
import { CmsBlogComponentImageCopyComponent } from './cms/blog/component/image-copy/cms-blog-component-image-copy.component';
import { CmsBlogComponentMoreToExploreComponent } from './cms/blog/component/more-to-explore/cms-blog-component-more-to-explore.component';
import { CmsBlogComponentTextComponent } from './cms/blog/component/text/cms-blog-component-text.component';
import { AccordionComponent } from './common/accordion/accordion.component';
import { AddressFieldsetComponent } from './common/address-fieldset/address-fieldset.component';
import { AddressLongComponent } from './common/address-long/address-long.component';
import { AddressNamePipe } from './common/address-name/address-name.pipe';
import { AddressShortComponent } from './common/address-short/address-short.component';
import { BadgeComponent } from './common/badge/badge.component';
import { BecomeProBannerComponent } from './common/become-pro-banner/become-pro-banner.component';
import { BottomPanelAreaDirective } from './common/bottom-panel/bottom-panel-area.directive';
import { BottomPanelComponent } from './common/bottom-panel/bottom-panel.component';
import { BoxComponent } from './common/box/box.component';
import { AppBusyStateComponent } from './common/busy-state/app-busy-state.component';
import { RouterAppBusyStateComponent } from './common/busy-state/router-app-busy-state.component';
import { StatusAppBusyStateComponent } from './common/busy-state/status-app-busy-state.component';
import { ButtonStyleComponent } from './common/buttons/button-style/button-style.component';
import { IconButtonComponent } from './common/buttons/icon-button/icon-button.component';
import { InlineButtonComponent } from './common/buttons/inline-button/inline-button.component';
import { CalendarComponent } from './common/calendar/calendar.component';
import { CustomerNamePipe } from './common/customer-name/customer-name.pipe';
import { DeliveryModalComponent } from './common/delivery/modal/delivery-modal.component';
import { DropdownCalendarComponent } from './common/dropdown-calendar/dropdown-calendar.component';
import { DropdownComponent } from './common/dropdown/dropdown.component';
import { ExpandableComponent } from './common/expandable/expandable.component';
import { MultisiteFlagComponent } from './common/flag/multisite-flag.component';
import { CheckboxComponent } from './common/form/checkbox/checkbox.component';
import { FormFieldComponent } from './common/form/field/form-field.component';
import { FormMuiFieldComponent } from './common/form/mui-field/form-mui-field.component';
import { RadioGroupItemComponent } from './common/form/radio/group/item/radio-group-item.component';
import { RadioGroupItem2Component } from './common/form/radio/group/item2/radio-group-item2.component';
import { RadioGroupComponent } from './common/form/radio/group/radio-group.component';
import { RadioComponent } from './common/form/radio/radio.component';
import { ToggleComponent } from './common/form/toggle/toggle.component';
import { GoBackComponent } from './common/go-back/go-back.component';
import { GoogleMapComponent } from './common/google-map/google-map.component';
import { IconLabelComponent } from './common/icon-label/icon-label.component';
import { IfCheckoutProcessDirective } from './common/if-checkout/if-checkout-process.directive';
import { IfNotCheckoutProcessDirective } from './common/if-checkout/if-not-checkout-process.directive';
import { ImageVideoComponent } from './common/image-video/image-video.component';
import { LineSeparatorComponent } from './common/line-separator/line-separator.component';
import { LinkList1Component } from './common/link-list1/link-list1.component';
import { Loader1Component } from './common/loader/loader-1.component';
import { LoaderComponent } from './common/loader/loader.component';
import { ModalLayoutMiddleComponent } from './common/modal/layout-middle/modal-layout-middle.component';
import { ModalLayoutComponent } from './common/modal/layout/modal-layout.component';
import { NewsletterCheckboxComponent } from './common/newsletter-checkbox/newsletter-checkbox.component';
import { NewsletterFormComponent } from './common/newsletter/newsletter-form.component';
import { NotificationModalComponent } from './common/notification/notification.modal.component';
import { OverlayComponent } from './common/overlay/overlay.component';
import { PasswordStrengthCheckerComponent } from './common/password-strength-checker/password-strength-checker.component';
import { PaymentIconComponent } from './common/payment-icon/payment-icon.component';
import { ProgressBarComponent } from './common/progress-bar/progress-bar.component';
import { RadioExpandableComponent } from './common/radio-expandable/radio-expandable.component';
import { RatingComponent } from './common/rating/rating.component';
import { ReadMoreComponent } from './common/read-more/read-more.component';
import { ResultComponent } from './common/result/result.component';
import { Section1Component } from './common/section1/section1.component';
import { Section2Component } from './common/section2/section2.component';
import { StickyFreeComponent } from './common/sticky-free/sticky-free.component';
import { TabsContentComponent } from './common/tabs/content/tabs-content.component';
import { TabsHeaderComponent } from './common/tabs/header/tabs-header.component';
import { TabsLinkComponent } from './common/tabs/link/tabs-link.component';
import { TaxParamModule } from './common/tax-param/tax-param.module';
import { TextComponent } from './common/text/text.component';
import { ViewMoreComponent } from './common/view-more/view-more.component';
import { ReviewsIOModule } from './module/reviews-io/reviews-io.module';
import { MultisiteGeolocationModalComponent } from './multisite/geolocation-modal/multisite-geolocation-modal.component';
import { MultisiteSwitchModalComponent } from './multisite/switch-modal/multisite-switch-modal.component';
import { PageHeaderShopSubmenuContentComponent } from './page/header/shop-submenu-content/page-header-shop-submenu-content.component';
import { DfModulePaypalModule } from '@df/module-paypal/df-module-paypal.module';

export const DECLARATIONS = [
    BecomeProBannerComponent,
    AddressShortComponent,
    AddressFieldsetComponent,
    AddressNamePipe,
    AppBusyStateComponent,
    BadgeComponent,
    BottomPanelAreaDirective,
    BottomPanelComponent,
    BoxComponent,
    CalendarComponent,
    DeliveryModalComponent,
    DropdownCalendarComponent,
    AccountHistoryInvoicesPaginationComponent,
    IconButtonComponent,
    PageHeaderShopSubmenuContentComponent,
    TextComponent,
    CheckboxComponent,
    // CmsModalOpenDirective,
    CustomerNamePipe,
    ViewMoreComponent,
    DropdownComponent,
    AccordionComponent,
    LoadMoreComponent,
    ProgressBarComponent,
    ExpandableComponent,
    CmsBlogComponentTextComponent,
    CmsBlogComponentImageCopyComponent,
    CmsBlogComponentMoreToExploreComponent,
    BranchCollectionComponent,
    GoBackComponent,
    GoogleMapComponent,
    ICmsOutletBlockComponent,
    IconLabelComponent,
    IfCheckoutProcessDirective,
    IfNotCheckoutProcessDirective,
    BranchFinderItemComponent,
    ImageVideoComponent,
    RemoveCouponComponent,
    BranchFinderComponent,
    PageStoreFinderStoreStatusComponent,
    PageStoreFinderStoreComponent,
    LineSeparatorComponent,
    LinkList1Component,
    ModalLayoutMiddleComponent,
    NewsletterFormComponent,
    NewsletterCheckboxComponent,
    MultisiteGeolocationModalComponent,
    MultisiteFlagComponent,
    MultisiteSwitchModalComponent,
    OverlayComponent,
    PasswordStrengthCheckerComponent,
    RadioComponent,
    RadioExpandableComponent,
    RadioGroupComponent,
    RadioGroupItemComponent,
    RadioGroupItem2Component,
    RatingComponent,
    ReadMoreComponent,
    ResultComponent,
    RouterAppBusyStateComponent,
    Section1Component,
    Section2Component,

    StatusAppBusyStateComponent,
    StickyFreeComponent,
    TabsContentComponent,
    TabsLinkComponent,
    TabsHeaderComponent,
    ToggleComponent,
    FormFieldComponent,
    FormMuiFieldComponent
];

const IMPORTS = [
    ButtonStyleComponent,
    InlineButtonComponent,
    IsWhitePipe,
    Loader1Component,
    ModalLayoutComponent,
    PricePipe,
    ToAddressPipe,
    IfAclDirective,
    IfDevDirective,
    HourFormat12Pipe,
    FindMediaByLabellPipe,
    FilterMediaByLabelPipe,
    OnInViewDirective,
    MatchCheckDirective,
    DecimalFormatDirective,
    ClickEventDirective,
    Nl2brPipe,
    ToggleClassDirective,
    ValidatePatternDirective,
    ValidateNumberDirective,
    AddressLongComponent,
    AtomicModule,
    AutoSizeInputDirective,
    CommonModule,
    GtmImpressionTrackDirective,
    GtmTrackProductClickDirective,
    CarouselModule,
    DragScrollDirective,
    FileUploadComponent,
    FormatDatePipe,
    FormsModule,
    HideUpComponent,
    InfiniteScrollModule,
    IfLoggedInDirective,
    IfNotLoggedInDirective,
    KlevuTrackCategoryProductClickDirective,
    ReactiveFormsModule,
    ScrollingModule,
    ModalModule,
    MediaModule,
    TaxParamModule,
    OffClickModule,
    PortalModule,
    VarsPipe,
    NoticePipe,
    OrderByPipe,
    LastCategoryPathPipe,
    PhotoSwipeModule,
    ZippyModule,
    LineHeightByComponent,
    OptionLabelPipe,
    RouterModule,
    RecentlyViewedModule,
    ReviewsIOModule,
    SwiperModule,
    ScrollToDirective,
    TranslationModule,
    DfCoreModule,
    DfDomModule,
    DfModulePrivacyModule,
    DfModuleImageModule,
    DfUiModule,
    HideUpComponent,
    HrefComponent,
    ICmsClientModule,
    DragonflyStylesModule,
    LoaderComponent,
    PaymentIconComponent,
    PriceComponent,
    ShellComponent,
    SizeClassDirective,
    StickyPanelDirective,
    TradePricingModule
];

@NgModule({
    imports: [
        ...IMPORTS,
        DfModulePaypalModule.forRoot(),
        DfUiModule.forRoot({
            date: {
                format: 'dd/MM/yyyy'
            }
        })
    ],
    declarations: DECLARATIONS,
    exports: [...IMPORTS, ...DECLARATIONS]
})
export class SharedModule {
    constructor() {
        surviveTreeShaking(NotificationModalComponent, DeliveryModalComponent);
    }
}
